<template>
    <div class="weather-sidebar" :style="{width: width + 'px'}">
        <div class="weather-sidebar-inner">
            <div class="weather-sidebar-top-row">
                <div class="weather-sidebar-search-btn" :class="{'weather-sidebar-search-btn--disabled': isMaxLocations}" v-if="!searchOpen" @click="!isMaxLocations && (searchOpen = true)">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><circle cx="10" cy="10" r="7"/><path d="m16 16 5 5"/></svg>
                    <span>{{ isMaxLocations ? $t('common.weather.maxLocations') : $t('common.weather.search') }}</span>
                </div>
                <WeatherCitySearch v-if="searchOpen" @select="onCitySelect" @close="searchOpen = false"/>
                <button v-if="!searchOpen" class="weather-sidebar-locate-btn" :class="{'weather-sidebar-locate-btn--disabled': isMaxLocations}" :disabled="isMaxLocations" @click="onDetect" :title="$t('common.weather.detectLocation')">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                </button>
            </div>

            <div class="weather-sidebar-list" :style="swipeX !== 0 ? {overflow: 'hidden'} : null">
                <div v-for="(loc, i) in locations" :key="i" class="weather-sidebar-card-swipe">
                    <div class="weather-sidebar-card-swipe-action weather-sidebar-card-swipe-action--default" v-show="swipingIndex === i && swipeX > 0">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="#fff" stroke="#fff" stroke-width="2"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
                    </div>
                    <div class="weather-sidebar-card-swipe-action weather-sidebar-card-swipe-action--delete" v-show="swipingIndex === i && swipeX < 0">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                    </div>
                    <div class="weather-sidebar-card"
                         :class="[{'weather-sidebar-card--active': i === activeIndex, 'weather-sidebar-card--swiping': swipingIndex === i}, loc.data?.current ? 'wsc--' + cardEffects[i] : '']"
                         :style="swipingIndex === i ? {transform: 'translateX(' + swipeX + 'px)', transition: 'none', borderRadius: 0} : null"
                         @click="onCardClick(i)"
                         @contextmenu.prevent="onContextMenu($event, i)"
                         @touchstart.passive="onLongPressStart($event, i)"
                         @touchmove.passive="onLongPressMove($event)"
                         @touchend.passive="onLongPressEnd($event)"
                         @pointerdown="onSwipeStart($event, i)"
                         @pointermove="onSwipeMove($event)"
                         @pointerup="onSwipeEnd($event, i)"
                         @pointercancel="onSwipeCancel">
                        <div class="weather-sidebar-card-fx" v-if="animationsEnabled && loc.data?.current">
                            <span v-if="cardEffects[i] === 'sunny'" class="wsc-fx-glow"></span>

                            <template v-else-if="cardEffects[i] === 'partcloud'">
                                <span v-for="j in 2" :key="'pc'+j" class="wsc-fx-cloud" :style="miniCloudStyle(j, i)"></span>
                            </template>

                            <template v-else-if="cardEffects[i] === 'cloud'">
                                <span v-for="j in 3" :key="'c'+j" class="wsc-fx-cloud wsc-fx-cloud--dense" :style="miniCloudStyle(j, i)"></span>
                            </template>

                            <template v-else-if="cardEffects[i] === 'rain'">
                                <span v-for="j in 14" :key="'r'+j" class="wsc-fx-drop" :style="miniRainStyle(j)"></span>
                            </template>

                            <template v-else-if="cardEffects[i] === 'thunder'">
                                <span v-for="j in 14" :key="'tr'+j" class="wsc-fx-drop wsc-fx-drop--heavy" :style="miniRainStyle(j)"></span>
                                <span class="wsc-fx-flash"></span>
                            </template>

                            <template v-else-if="cardEffects[i] === 'snow'">
                                <span v-for="j in 12" :key="'s'+j" class="wsc-fx-flake" :style="miniSnowStyle(j)"></span>
                            </template>

                            <template v-else-if="cardEffects[i] === 'fog'">
                                <span v-for="j in 2" :key="'f'+j" class="wsc-fx-fog" :style="miniFogStyle(j)"></span>
                            </template>

                            <template v-else-if="cardEffects[i] === 'night'">
                                <span v-for="j in 5" :key="'st'+j" class="wsc-fx-star" :style="miniStarStyle(j)"></span>
                            </template>

                            <template v-else-if="cardEffects[i] === 'night_few'">
                                <span v-for="j in 4" :key="'st'+j" class="wsc-fx-star" :style="miniStarStyle(j)"></span>
                                <span class="wsc-fx-cloud wsc-fx-cloud--night" :style="miniCloudStyle(1, i)"></span>
                            </template>

                            <template v-else-if="cardEffects[i] === 'night_partcloud'">
                                <span v-for="j in 3" :key="'st'+j" class="wsc-fx-star" :style="miniStarStyle(j)"></span>
                                <span v-for="j in 2" :key="'nc'+j" class="wsc-fx-cloud wsc-fx-cloud--night" :style="miniCloudStyle(j, i)"></span>
                            </template>

                            <template v-else-if="cardEffects[i] === 'night_cloud'">
                                <span v-for="j in 2" :key="'st'+j" class="wsc-fx-star" :style="miniStarStyle(j)"></span>
                                <span v-for="j in 3" :key="'nc'+j" class="wsc-fx-cloud wsc-fx-cloud--night wsc-fx-cloud--dense" :style="miniCloudStyle(j, i)"></span>
                            </template>
                        </div>
                        <div class="weather-sidebar-card-top">
                            <div class="weather-sidebar-card-city">
                                {{ formatCity(loc) }}
                                <span v-if="i === defaultIndex" class="weather-sidebar-card-default">{{ $t('common.weather.myLocation') }}</span>
                            </div>
                            <div class="weather-sidebar-card-temp">
                                {{ loc.data?.current?.temperature != null ? Math.round(loc.data.current.temperature) + '°' : '--' }}
                            </div>
                        </div>
                        <div class="weather-sidebar-card-bottom">
                            <span class="weather-sidebar-card-condition">{{ loc.data?.current?.condition?.label || '' }}</span>
                            <span class="weather-sidebar-card-hilo" v-if="loc.data?.daily?.[0]">
                                {{ $t('common.weather.high') }}:{{ Math.round(loc.data.daily[0].temp_max) }}°
                                {{ $t('common.weather.low') }}:{{ Math.round(loc.data.daily[0].temp_min) }}°
                            </span>
                        </div>
                    </div>
                </div>
            </div>

        </div>

        <div class="weather-sidebar-divider"
             @mousedown="$emit('divider-start', $event)"
             @touchstart.prevent="$emit('divider-start', $event)"></div>

        <Teleport to="body">
            <div v-if="ctxMenu.visible" class="weather-ctx-menu" :style="ctxMenuStyle">
                <button v-if="ctxMenu.index !== defaultIndex" class="weather-ctx-item" @click="onSetDefault">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
                    {{ $t('common.weather.setDefault') }}
                </button>
                <button class="weather-ctx-item weather-ctx-item--danger" @click="onRemove">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                    {{ $t('common.weather.removeLocation') }}
                </button>
            </div>
            <div v-if="ctxMenu.visible" style="position:fixed;inset:0;z-index:200000" @click="ctxMenu.visible = false" @contextmenu.prevent="ctxMenu.visible = false"></div>
        </Teleport>
    </div>
</template>

<script>
import weatherService from './weatherService'
import WeatherCitySearch from './WeatherCitySearch.vue'

const ICON_EFFECT = {
    thunderstorm: 'thunder',
    rain: 'rain', rain_shower: 'rain', freezing_rain: 'rain',
    drizzle: 'rain', freezing_drizzle: 'rain',
    snow: 'snow', snow_grains: 'snow', snow_shower: 'snow',
    fog: 'fog',
    clear: 'sunny', mainly_clear: 'sunny', night_clear: 'night',
    partly_cloudy: 'partcloud',
    overcast: 'cloud',
}

export default {
    name: 'WeatherSidebar',
    components: { WeatherCitySearch },
    emits: ['select', 'divider-start'],
    props: {
        width: { type: Number, default: 240 },
        activeIndex: { type: Number, default: 0 },
        animationsEnabled: { type: Boolean, default: true },
    },
    data() {
        return {
            searchOpen: false,
            detecting: false,
            ctxMenu: { visible: false, x: 0, y: 0, index: -1 },
            swipingIndex: null,
            swipeX: 0,
        }
    },
    computed: {
        locations() {
            return weatherService.state.locations
        },
        defaultIndex() {
            return weatherService.state.defaultIndex
        },
        isMaxLocations() {
            return this.locations.length >= 10
        },
        cardEffects() {
            return this.locations.map(loc => this.getCardEffect(loc))
        },
        ctxMenuStyle() {
            return {
                position: 'fixed',
                left: this.ctxMenu.x + 'px',
                top: this.ctxMenu.y + 'px',
                zIndex: 200001,
            }
        },
    },
    methods: {
        onCitySelect(city) {
            weatherService.addLocation(city.cityId, city.districtId, city.cityName, city.districtName)
            this.searchOpen = false
        },
        async onDetect() {
            if (this.detecting) return
            this.detecting = true
            try {
                await weatherService.detectLocation()
            } catch { /* empty */ }
            this.detecting = false
        },
        onCardClick(i) {
            if (this._longPressed) {
                this._longPressed = false
                return
            }
            if (this._swiped) {
                this._swiped = false
                return
            }
            this.$emit('select', i)
        },
        onContextMenu(e, index) {
            this.ctxMenu = { visible: true, x: e.clientX, y: e.clientY, index }
        },
        onSetDefault() {
            weatherService.setDefault(this.ctxMenu.index)
            this.ctxMenu.visible = false
        },
        onRemove() {
            const idx = this.ctxMenu.index
            this.ctxMenu.visible = false
            this._confirmRemove(idx)
        },

        onLongPressStart(e, index) {
            const touch = e.touches[0]
            this._lpStartX = touch.clientX
            this._lpStartY = touch.clientY
            this._longPressed = false
            this._longPressReady = false
            this._lpIndex = index
            this._longPressTimer = setTimeout(() => {
                this._longPressReady = true
                this._longPressTimer = null
                this._vibrate(15)
            }, 350)
        },
        onLongPressMove(e) {
            if (!this._longPressTimer && !this._longPressReady) return
            const touch = e.touches[0]
            const dx = Math.abs(touch.clientX - this._lpStartX)
            const dy = Math.abs(touch.clientY - this._lpStartY)
            if (dx > 20 || dy > 20) {
                clearTimeout(this._longPressTimer)
                this._longPressTimer = null
                this._longPressReady = false
            }
        },
        onLongPressEnd(e) {
            clearTimeout(this._longPressTimer)
            this._longPressTimer = null
            if (this._longPressReady) {
                const touch = e.changedTouches[0]
                const dx = Math.abs(touch.clientX - this._lpStartX)
                const dy = Math.abs(touch.clientY - this._lpStartY)
                if (dx < 40 && dy < 40) {
                    this._longPressReady = false
                    this._longPressed = true
                    this.onContextMenu({ clientX: this._lpStartX, clientY: this._lpStartY }, this._lpIndex)
                    return
                }
            }
            this._longPressReady = false
        },

        onSwipeStart(e, index) {
            if (e.button && e.button !== 0) return
            this._swipeStartX = e.clientX
            this._swipeStartY = e.clientY
            this._swipeLocked = false
            this._swiped = false
            this._swipingIdx = index
            this._isSwipeGesture = false
            this._swipePointerType = e.pointerType
            this._swipeOverThreshold = false
        },
        onSwipeMove(e) {
            if (this._swipeStartX == null) return
            const dx = e.clientX - this._swipeStartX
            const dy = e.clientY - this._swipeStartY

            if (!this._swipeLocked) {
                if (Math.abs(dx) < 3 && Math.abs(dy) < 3) return
                if (this._swipePointerType === 'mouse' ? Math.abs(dy) > Math.abs(dx) * 1.5 : Math.abs(dy) >= Math.abs(dx)) {
                    return
                }
                this._swipeLocked = true
                this._isSwipeGesture = true
                this._swiped = true
                this.swipingIndex = this._swipingIdx
                clearTimeout(this._longPressTimer)
                this._longPressReady = false
                if (this._swipePointerType === 'mouse') {
                    document.addEventListener('mousemove', this._onDocMouseMove)
                    document.addEventListener('mouseup', this._onDocMouseUp)
                } else {
                    document.addEventListener('pointermove', this._onDocPointerMove)
                    document.addEventListener('pointerup', this._onDocPointerUp)
                }
            }

            if (this._swipePointerType !== 'mouse') e.preventDefault()
            const maxSwipe = 120
            this.swipeX = Math.max(-maxSwipe, Math.min(maxSwipe, dx))
            this._checkSwipeThreshold()
        },
        _onDocMouseMove(e) {
            if (!this._swipeLocked || this._swipeStartX == null) return
            const dx = e.clientX - this._swipeStartX
            const maxSwipe = 120
            this.swipeX = Math.max(-maxSwipe, Math.min(maxSwipe, dx))
            this._checkSwipeThreshold()
        },
        _onDocMouseUp() {
            document.removeEventListener('mousemove', this._onDocMouseMove)
            document.removeEventListener('mouseup', this._onDocMouseUp)
            if (this._swipeLocked) this._resolveSwipe()
            this._swipeStartX = null
            this._swipeLocked = false
            this._isSwipeGesture = false
        },
        _onDocPointerMove(e) {
            if (!this._swipeLocked || this._swipeStartX == null) return
            const dx = e.clientX - this._swipeStartX
            const maxSwipe = 120
            this.swipeX = Math.max(-maxSwipe, Math.min(maxSwipe, dx))
            this._checkSwipeThreshold()
        },
        _onDocPointerUp() {
            document.removeEventListener('pointermove', this._onDocPointerMove)
            document.removeEventListener('pointerup', this._onDocPointerUp)
            if (this._swipeLocked) this._resolveSwipe()
            this._swipeStartX = null
            this._swipeLocked = false
            this._isSwipeGesture = false
        },
        onSwipeEnd(e, index) {
            if (this._swipeStartX == null && !this._swipeLocked) return
            if (this._swipePointerType === 'mouse' && this._swipeLocked) return
            if (!this._swipeLocked) {
                this._swipeStartX = null
                this.swipingIndex = null
                return
            }
            document.removeEventListener('pointermove', this._onDocPointerMove)
            document.removeEventListener('pointerup', this._onDocPointerUp)
            this._resolveSwipe()
            this._swipeStartX = null
            this._swipeLocked = false
            this._isSwipeGesture = false
        },
        onSwipeCancel() {
            if (this._isSwipeGesture) return
            this.swipeX = 0
            this.swipingIndex = null
        },
        _resolveSwipe() {
            const dx = this.swipeX
            const idx = this._swipingIdx
            const threshold = 60
            this.swipeX = 0
            this.swipingIndex = null
            if (dx > threshold && idx !== this.defaultIndex) {
                weatherService.setDefault(idx)
            } else if (dx < -threshold) {
                this._confirmRemove(idx)
            }
        },
        _confirmRemove(idx) {
            const loc = this.locations[idx]
            const name = this.formatCity(loc)
            this.eventBus.$emit('showConfirmDialog', {
                title: this.$t('common.weather.removeLocation'),
                message: name + ' ' + this.$t('common.weather.removeConfirm'),
                confirmText: this.$t('common.weather.removeLocation'),
                cancelText: this.$t('common.cancel'),
                confirmType: 'danger',
                onConfirm: () => weatherService.removeLocation(idx),
            })
        },
        _vibrate(ms) {
            if (navigator.vibrate) navigator.vibrate(ms)
        },
        _checkSwipeThreshold() {
            const isOver = Math.abs(this.swipeX) >= 60
            if (isOver !== this._swipeOverThreshold) {
                this._vibrate(10)
                this._swipeOverThreshold = isOver
            }
        },
        formatCity(loc) {
            if (loc.districtName && loc.cityName && loc.districtName !== loc.cityName) {
                return loc.cityName + ', ' + loc.districtName
            }
            return loc.cityName || loc.districtName || ''
        },
        isLocNight(loc) {
            const today = loc.data?.daily?.[0]
            if (!today?.sunrise || !today?.sunset) return false
            const now = new Date()
            const [sh, sm] = today.sunrise.split(':').map(Number)
            const [eth, etm] = today.sunset.split(':').map(Number)
            const mins = now.getHours() * 60 + now.getMinutes()
            return mins < sh * 60 + sm || mins > eth * 60 + etm
        },
        getCardEffect(loc) {
            const current = loc.data?.current
            if (!current) return this.isLocNight(loc) ? 'night' : 'sunny'
            let effect
            const icon = current.condition?.icon || current.condition_icon
            if (icon && ICON_EFFECT[icon]) {
                effect = ICON_EFFECT[icon]
            } else {
                const id = Number(current.condition?.id || current.condition_id) || 0
                if (id) {
                    if ([95, 96, 99].includes(id)) effect = 'thunder'
                    else if ([51, 53, 55, 56, 57, 61, 63, 65, 66, 67, 80, 81, 82].includes(id)) effect = 'rain'
                    else if ([71, 73, 75, 77, 85, 86].includes(id)) effect = 'snow'
                    else if ([45, 48].includes(id)) effect = 'fog'
                    else if ([0, 1].includes(id)) effect = 'sunny'
                    else if (id === 2) effect = 'partcloud'
                    else if (id === 3) effect = 'cloud'
                    else effect = 'sunny'
                } else {
                    effect = 'sunny'
                }
            }
            if (this.isLocNight(loc)) {
                const ic = current?.condition?.icon || current?.condition_icon || ''
                if (ic === 'mainly_clear' || (effect === 'sunny' && ic !== 'clear')) return 'night_few'
                if (effect === 'sunny') return 'night'
                if (effect === 'partcloud') return 'night_partcloud'
                if (effect === 'cloud') return 'night_cloud'
            }
            return effect
        },
        miniCloudStyle(j, cardIdx) {
            const seed = (cardIdx || 0) * 7 + j * 13
            const top = 15 + ((seed * 17.3 + seed * seed * 1.7) % 50)
            const left = 5 + ((seed * 23.1 + seed * seed * 2.3) % 70)
            const w = 30 + ((seed * 11) % 30)
            const h = Math.round(w * 0.3)
            return {
                top: top + '%', left: left + '%',
                width: w + 'px', height: h + 'px',
                animationDuration: (5 + (seed % 6)) + 's',
                animationDelay: -((seed * 0.7) % 5) + 's',
            }
        },
        miniRainStyle(j) {
            return {
                left: ((j * 13.7 + j * j * 2.3) % 90 + 5) + '%',
                animationDelay: -(0.4 * ((j * 0.37) % 1)) + 's',
                animationDuration: (0.4 + (j % 3) * 0.05) + 's',
            }
        },
        miniSnowStyle(j) {
            return {
                left: ((j * 17.3 + j * j * 3.1) % 85 + 5) + '%',
                animationDelay: ((j * 0.3) % 2) + 's',
                animationDuration: (1.5 + (j % 3) * 0.4) + 's',
            }
        },
        miniStarStyle(j) {
            const top = 10 + ((j * 23.7 + j * j * 5.3) % 60)
            const left = 5 + ((j * 19.3 + j * j * 3.1) % 85)
            const size = 1.5 + (j % 2)
            return {
                top: top + '%', left: left + '%',
                width: size + 'px', height: size + 'px',
                animationDuration: (1.5 + (j % 4) * 0.5) + 's',
                animationDelay: (j * 0.4) + 's',
            }
        },
        miniFogStyle(j) {
            return {
                top: (10 + j * 30) + '%',
                animationDuration: (5 + j * 2) + 's',
                animationDelay: (j * 1.5) + 's',
                animationDirection: j % 2 === 0 ? 'normal' : 'reverse',
            }
        },
    },
    beforeUnmount() {
        clearTimeout(this._longPressTimer)
        document.removeEventListener('pointermove', this._onDocPointerMove)
        document.removeEventListener('pointerup', this._onDocPointerUp)
        document.removeEventListener('mousemove', this._onDocMouseMove)
        document.removeEventListener('mouseup', this._onDocMouseUp)
    },
}
</script>
